export interface WalletTransactionDetail {
  timestamp: string;
  status: string;
  data: WalletTransactionDetailData;
  error?: WalletTransactionDetailError;
  errors?: WalletTransactionDetailError[];
  page?: number;
  metadata?: Record<string, any>;
  translations?: Record<string, string>;
  total_pages?: number;
  per_page?: number;
  total_items?: number;
}

export interface WalletTransactionDetailData {
  roundNumber: number;
  totalRounds: number;
  saleType: string;
  saleId: string;
  status: WalletTransactionDetailStatus;
  saleName: string;
  imageHash: string;
  artistName: string;
  unitPrice: number;
  paymentDate: string;
  startDate: string;
  endDate: string;
  unitsSold: number;
  totalUnits: number;
  totalInvestors: number;
  totalRaised: number;
  currencyType: WalletTransactionDetailCurrencyType;
  summary: WalletTransactionDetailSummary;
}

export interface WalletTransactionDetailSummary {
  totalGross: number;
  salesTax: number;
  temporaryReserve: number;
  netReceived: number;
  currencyType: WalletTransactionDetailCurrencyType;
  salesTaxPct: number;
}

export interface WalletTransactionDetailError {
  error: string;
  title: string;
  code: string;
  trace: string;
  metadata: Record<string, any>;
  description: string;
  modal: boolean;
}

export type WalletTransactionDetailStatus =
  | 'UNDER_INSPECTION'
  | 'COMPLETED'
  | 'PENDING'
  | 'CANCELLED'
  | 'FAILED';

export type WalletTransactionDetailCurrencyType =
  | 'AED'
  | 'AFN'
  | 'ALL'
  | 'AMD'
  | 'ANG'
  | 'AOA'
  | 'ARS'
  | 'AUD'
  | 'AWG'
  | 'AZN'
  | 'BAM'
  | 'BBD'
  | 'BDT'
  | 'BGN'
  | 'BHD'
  | 'BIF'
  | 'BMD'
  | 'BND'
  | 'BOB'
  | 'BRL'
  | 'BSD'
  | 'BTN'
  | 'BWP'
  | 'BYN'
  | 'BZD'
  | 'CAD'
  | 'CDF'
  | 'CHF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'CRC'
  | 'CUP'
  | 'CVE'
  | 'CZK'
  | 'DJF'
  | 'DKK'
  | 'DOP'
  | 'DZD'
  | 'EGP'
  | 'ERN'
  | 'ETB'
  | 'EUR'
  | 'FJD'
  | 'FKP'
  | 'FOK'
  | 'GBP'
  | 'GEL'
  | 'GGP'
  | 'GHS'
  | 'GIP'
  | 'GMD'
  | 'GNF'
  | 'GTQ'
  | 'GYD'
  | 'HKD'
  | 'HNL'
  | 'HRK'
  | 'HTG'
  | 'HUF'
  | 'IDR'
  | 'ILS'
  | 'IMP'
  | 'INR'
  | 'IQD'
  | 'IRR'
  | 'ISK'
  | 'JEP'
  | 'JMD'
  | 'JOD'
  | 'JPY'
  | 'KES'
  | 'KGS'
  | 'KHR'
  | 'KID'
  | 'KMF'
  | 'KRW'
  | 'KWD'
  | 'KYD'
  | 'KZT'
  | 'LAK'
  | 'LBP'
  | 'LKR'
  | 'LRD'
  | 'LSL'
  | 'LYD'
  | 'MAD'
  | 'MDL'
  | 'MGA'
  | 'MKD'
  | 'MMK'
  | 'MNT'
  | 'MOP'
  | 'MRU'
  | 'MUR'
  | 'MVR'
  | 'MWK'
  | 'MXN'
  | 'MYR'
  | 'MZN'
  | 'NAD'
  | 'NGN'
  | 'NIO'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'OMR'
  | 'PAB'
  | 'PEN'
  | 'PGK'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'PYG'
  | 'QAR'
  | 'RON'
  | 'RSD'
  | 'RUB'
  | 'RWF'
  | 'SAR'
  | 'SBD'
  | 'SCR'
  | 'SDG'
  | 'SEK'
  | 'SGD'
  | 'SHP'
  | 'SLE'
  | 'SLL'
  | 'SOS'
  | 'SRD'
  | 'SSP'
  | 'STN'
  | 'SYP'
  | 'SZL'
  | 'THB'
  | 'TJS'
  | 'TMT'
  | 'TND'
  | 'TOP'
  | 'TRY'
  | 'TTD'
  | 'TVD'
  | 'TWD'
  | 'TZS'
  | 'UAH'
  | 'UGX'
  | 'USD'
  | 'UYU'
  | 'UZS'
  | 'VED'
  | 'VES'
  | 'VND'
  | 'VUV'
  | 'WST'
  | 'XAF'
  | 'XCD'
  | 'XDR'
  | 'XOF'
  | 'XPF'
  | 'YER'
  | 'ZAR'
  | 'ZMW'
  | 'ZWL';

export function createEmptyWalletTransactionDetailData(): WalletTransactionDetailData {
  return {
    roundNumber: 0,
    totalRounds: 0,
    saleType: '',
    saleId: '',
    status: 'PENDING',
    saleName: '',
    imageHash: '',
    artistName: '',
    unitPrice: 0,
    paymentDate: '',
    startDate: '',
    endDate: '',
    unitsSold: 0,
    totalUnits: 0,
    totalInvestors: 0,
    totalRaised: 0,
    currencyType: 'USD',
    summary: {
      totalGross: 0,
      salesTax: 0,
      temporaryReserve: 0,
      netReceived: 0,
      currencyType: 'USD',
      salesTaxPct: 0,
    },
  };
}
